import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const FAQ = () => {
  const [openKey, setOpenKey] = useState(null);
  const { t } = useLang();
  const F = t.faq;

  const toggle = (key) => {
    setOpenKey((k) => (k === key ? null : key));
  };

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 md:py-32 bg-navy-50/40"
    >
      <div className="container-vel">
        <div className="max-w-3xl mb-16">
          <div className="overline mb-5" data-testid="faq-overline">
            {F.overline}
          </div>
          <h2
            data-testid="faq-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-navy font-light leading-[1.05] tracking-tight"
          >
            {F.headlinePre}{" "}
            <em className="not-italic underline decoration-coral decoration-2 underline-offset-8">
              {F.headlineHighlight}
            </em>
            {F.headlinePost}
          </h2>
          <p className="mt-6 font-body text-base sm:text-lg text-ink-muted leading-relaxed font-light">
            {F.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <nav
              data-testid="faq-category-nav"
              className="sticky top-28 space-y-1"
            >
              {F.categories.map((cat) => (
                <a
                  key={cat.id}
                  data-testid={`faq-cat-link-${cat.id}`}
                  href={`#faq-${cat.id}`}
                  className="block font-body text-sm tracking-wide text-ink-muted hover:text-coral border-l-2 border-navy/15 hover:border-coral pl-4 py-2 transition-colors duration-300"
                >
                  {cat.title}
                </a>
              ))}
            </nav>
          </div>

          <div className="lg:col-span-8 space-y-14">
            {F.categories.map((cat) => (
              <div
                key={cat.id}
                id={`faq-${cat.id}`}
                data-testid={`faq-category-${cat.id}`}
                className="scroll-mt-28"
              >
                <div className="flex items-baseline gap-4 mb-6">
                  <h3 className="font-heading text-2xl sm:text-3xl text-navy font-medium">
                    {cat.title}
                  </h3>
                </div>

                <div className="border-t border-navy/15 bg-white">
                  {cat.items.map((item, idx) => {
                    const key = `${cat.id}-${idx}`;
                    const isOpen = openKey === key;
                    return (
                      <div
                        key={key}
                        data-testid={`faq-item-${cat.id}-${idx}`}
                        className="border-b border-navy/15"
                      >
                        <button
                          data-testid={`faq-toggle-${cat.id}-${idx}`}
                          onClick={() => toggle(key)}
                          aria-expanded={isOpen}
                          className="w-full flex items-start justify-between gap-6 text-left px-6 md:px-8 py-5 hover:bg-navy-50/40 transition-colors duration-300"
                        >
                          <span className="font-heading text-base sm:text-lg text-navy font-medium leading-snug">
                            {item.q}
                          </span>
                          <span className="shrink-0 mt-1 text-coral">
                            {isOpen ? (
                              <Minus size={18} strokeWidth={1.75} />
                            ) : (
                              <Plus size={18} strokeWidth={1.75} />
                            )}
                          </span>
                        </button>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              data-testid={`faq-answer-${cat.id}-${idx}`}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.35, ease: "easeOut" }}
                              className="overflow-hidden"
                            >
                              <p className="px-6 md:px-8 pb-6 font-body text-sm sm:text-base text-ink-muted leading-relaxed font-light">
                                {item.a}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
